import { Trans, t } from "@web-monorepo/i18n/macro";
import { DDSBodyText, DDSHeadlineText } from "@web-monorepo/dds";
import { getImageSrc } from "@web-monorepo/static-assets";
import { appMetadataRadio } from "src/pods/radio";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import MaterialIcon from "src/shared/components/MaterialIcon";
import Audio from "./Audio";
import styles from "./RadioRemote.module.css";
import { useRadioRemote, type UseRadioRemoteProps, type UseRadioRemoteResult } from "./useRadioRemote.vm";

const radio = getImageSrc("toolkit/src/pods/radio/assets/blue-radio.png");

type RadioRemoteProps = UseRadioRemoteProps & {
  classId: string;
  mountPoint: string;
  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state. Production callers leave the
   * default.
   */
  di?: {
    useVm?: (props: UseRadioRemoteProps) => UseRadioRemoteResult;
  };
};

export default function RadioRemote({ di, classId, mountPoint, ...rest }: RadioRemoteProps) {
  const useVm = di?.useVm ?? useRadioRemote;
  const {
    channels,
    channel,
    playing,
    isCasting,
    hasAudioSrc,
    audioSrc,
    openDisplay,
    closeDisplay,
    togglePlayback,
    nextTrack,
    previousTrack,
    startDisplayCast,
    stopDisplayCast,
  } = useVm(rest);

  return (
    <AppPage {...appMetadataRadio()} classId={classId} mountPoint={mountPoint}>
      <section className={styles.container}>
        <DDSHeadlineText as="h1" level="sm" className={styles.headerTitle}>
          <Trans>Choose a channel to play music!</Trans>
        </DDSHeadlineText>
        <ul className={styles.channels}>
          {channels.map((c) => (
            <li key={c.title}>
              <button
                type="button"
                data-name="radio_remote:render_channel:img"
                onClick={() => openDisplay(c)}
                className={styles.channel}
              >
                <img src={c.icon} className={styles.channelIcon} alt="" />
                <DDSBodyText className={styles.channelTitle}>{c.title}</DDSBodyText>
              </button>
            </li>
          ))}
        </ul>
        {channel && mountPoint === "mobile" && (
          <AppModal
            {...appMetadataRadio()}
            onRequestHide={closeDisplay}
            onRequestStart={startDisplayCast}
            onRequestStop={stopDisplayCast}
          >
            <section>
              <DDSBodyText className={styles.channelHeader}>
                <Trans>
                  Channel: <span className={styles.channelHeaderTitle}>{channel.title}</span>
                </Trans>
              </DDSBodyText>
              <button
                type="button"
                aria-label={t`Toggle playback`}
                data-name="radio_remote:toggle_playback"
                className={styles.togglePlaybackButton}
                onClick={togglePlayback}
              >
                <img className={styles.radioImg} src={radio} alt="" />
              </button>

              <div className={styles.playbackControl}>
                {/*
                 * Preserved pre-migration behavior: the "skip_previous" icon button advances
                 * forward via nextTrack() and the "skip_next" icon button rewinds via
                 * previousTrack(). Looks like a long-standing label/handler swap but
                 * fixing it is out of scope for the migration; flagged for a follow-up.
                 */}
                <button
                  type="button"
                  data-name="radio_remote:skip_previous"
                  className={styles.skipPreviousButton}
                  onClick={nextTrack}
                  aria-label={t`Previous`}
                >
                  <MaterialIcon icon="skip_previous" className={styles.icon} />
                </button>
                <button
                  type="button"
                  data-name="radio_remote:play_pause"
                  className={styles.playPauseButton}
                  onClick={togglePlayback}
                  aria-label={playing ? t`Pause` : t`Play`}
                >
                  <MaterialIcon icon={playing ? "pause" : "play_arrow"} />
                </button>
                <button
                  type="button"
                  data-name="radio_remote:skip_next"
                  className={styles.skipNextButton}
                  onClick={previousTrack}
                  aria-label={t`Next`}
                >
                  <MaterialIcon icon="skip_next" className={styles.icon} />
                </button>
              </div>
            </section>
            {!isCasting && hasAudioSrc && audioSrc && <Audio src={audioSrc} isPlaying={playing} />}
          </AppModal>
        )}
      </section>
    </AppPage>
  );
}
